import { useState, useEffect, useCallback } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/**
 * Hook que obtiene photoURL, nombre y metadata de "first login" del usuario actual.
 *
 * Bloque 7 del rediseño: la primera vez que el residente entra a la plataforma
 * (después de activar), se escribe `firstLoginAt = serverTimestamp()` en su
 * doc users. Componentes que quieran mostrar onboarding usan `isFirstLogin`
 * para condicionar UI.
 *
 * @returns {{ photoURL, displayName, initial, loading, refresh, isFirstLogin }}
 */
export function useUserProfile() {
  const [photoURL, setPhotoURL] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  // Listen for profile updates from other components
  useEffect(() => {
    const handler = () => setVersion((v) => v + 1);
    window.addEventListener("habitia:profile-updated", handler);
    return () => window.removeEventListener("habitia:profile-updated", handler);
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user?.email) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const q = query(
          collection(db, "users"),
          where("email", "==", user.email)
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          setDisplayName(user.displayName || user.email?.split("@")[0] || "");
          setLoading(false);
          return;
        }
        const docSnap = snap.docs[0];
        const d = docSnap.data();
        setPhotoURL(d.photoURL ?? d.avatar ?? "");
        setDisplayName(
          d.NombreCompleto ?? d.name ?? d.displayName ?? user.email?.split("@")[0] ?? ""
        );

        // Detectar primer login: si no hay firstLoginAt, este es el primer
        // acceso post-activación. Lo marcamos y exponemos como `isFirstLogin`
        // para que el dashboard pueda mostrar onboarding.
        if (!d.firstLoginAt) {
          setIsFirstLogin(true);
          try {
            await updateDoc(doc(db, "users", docSnap.id), {
              firstLoginAt: serverTimestamp(),
            });
          } catch (err) {
            console.warn("[useUserProfile] No se pudo escribir firstLoginAt:", err);
          }
        } else {
          // Considerar "primer login" hasta 24h después del primero, para que
          // si el residente cierra y vuelve a entrar siga viendo el banner.
          const firstAt = d.firstLoginAt?.toDate?.() ?? null;
          if (firstAt) {
            const diff = Date.now() - firstAt.getTime();
            setIsFirstLogin(diff < 24 * 60 * 60 * 1000);
          }
        }
      } catch (err) {
        console.error("Error al cargar perfil:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [version]);

  const initial = (displayName?.[0] || "?").toUpperCase();

  return { photoURL, displayName, initial, loading, refresh, isFirstLogin };
}
